/**
 * Canonical membership / board.frames id.
 * Strips annotation-style `shape:frame-` prefixes so AI-copied ids match createShapeId(`frame-${id}`).
 * Stock tldraw frames (`shape:<uuid>`) keep their full shape id.
 */
export function normalizeLogicalFrameId(id) {
  if (id == null || id === '') return null;
  const raw = String(id).trim();
  if (!raw) return null;

  const aiPrefixed = 'shape:frame-';
  if (raw.startsWith(aiPrefixed)) {
    return raw.slice(aiPrefixed.length) || null;
  }
  if (raw.startsWith('shape:')) {
    return raw;
  }
  return raw;
}

/** Match tldraw createShapeId(`frame-${logicalId}`) without importing the package. */
export function frameShapeIdFromLogicalId(logicalId) {
  const id = normalizeLogicalFrameId(logicalId);
  if (!id) return null;
  if (id.startsWith('shape:')) return id;
  return `shape:frame-${id}`;
}

/**
 * Extract the canonical logical id from an AI frame shape id (`shape:frame-${logicalId}`).
 * Returns null for stock tldraw frames and non-frame ids.
 */
export function logicalFrameIdFromShapeId(shapeId) {
  const raw = String(shapeId || '');
  if (!raw) return null;

  const aiPrefixed = 'shape:frame-';
  if (raw.startsWith(aiPrefixed)) {
    const logical = raw.slice(aiPrefixed.length);
    return logical || null;
  }

  // Legacy: ids that embed `frame-` after a colon (but not pin/other shapes).
  const marker = ':frame-';
  const idx = raw.indexOf(marker);
  if (idx >= 0) {
    const logical = raw.slice(idx + marker.length);
    return logical || null;
  }

  if (raw.startsWith('frame-')) {
    const logical = raw.slice('frame-'.length);
    return logical || null;
  }

  return null;
}

/**
 * Stable membership key stored on pin meta.frameId / placement.frameId.
 * - AI frames: meta.boardFrameId or shape:frame-${logicalId} → logicalId
 * - Stock tldraw frames: full shape id (e.g. shape:uuid)
 */
export function membershipIdFromFrameShape(frameShape) {
  if (!frameShape?.id || frameShape.type !== 'frame') return null;
  const fromMeta = normalizeLogicalFrameId(frameShape.meta?.boardFrameId);
  if (fromMeta) return fromMeta;
  return logicalFrameIdFromShapeId(frameShape.id) || String(frameShape.id);
}

function pointInBounds(x, y, bounds) {
  if (!bounds) return false;
  const w = bounds.w ?? 0;
  const h = bounds.h ?? 0;
  return x >= bounds.x && x <= bounds.x + w && y >= bounds.y && y <= bounds.y + h;
}

function readPageBounds(editor, shape) {
  const bounds = editor.getShapePageBounds?.(shape);
  if (!bounds || !Number.isFinite(bounds.x) || !Number.isFinite(bounds.y)) return null;
  return {
    x: bounds.x,
    y: bounds.y,
    w: bounds.w ?? 0,
    h: bounds.h ?? 0,
  };
}

/**
 * Page bounds for a frame record.
 * tldraw's bounds cache is keyed by id and already reflects the updated shape,
 * so a stale drag `prev` record is reconstructed from its own x/y.
 */
function framePageBounds(editor, frame) {
  const recordBounds = {
    x: frame.x ?? 0,
    y: frame.y ?? 0,
    w: frame.props?.w ?? 0,
    h: frame.props?.h ?? 0,
  };
  const live = editor.getShape?.(frame.id);
  const recordIsStale = Boolean(
    live && (
      live.x !== frame.x ||
      live.y !== frame.y ||
      (live.props?.w ?? 0) !== (frame.props?.w ?? 0) ||
      (live.props?.h ?? 0) !== (frame.props?.h ?? 0)
    )
  );

  if (recordIsStale) {
    const liveBounds = readPageBounds(editor, live);
    if (!liveBounds) return recordBounds;
    return {
      x: liveBounds.x + (frame.x - live.x),
      y: liveBounds.y + (frame.y - live.y),
      w: frame.props?.w ?? liveBounds.w,
      h: frame.props?.h ?? liveBounds.h,
    };
  }

  return readPageBounds(editor, frame) || recordBounds;
}

function isBoardPinShape(shape) {
  return (
    shape?.type === 'pin-card' ||
    shape?.type === 'suggest-pin' ||
    shape?.type === 'conversation-pin'
  );
}

function pinPageCenter(editor, shape) {
  const bounds = editor.getShapePageBounds?.(shape);
  if (bounds) {
    return { x: bounds.x + bounds.w / 2, y: bounds.y + bounds.h / 2 };
  }
  return {
    x: shape.x + (shape.props?.w ?? 0) / 2,
    y: shape.y + (shape.props?.h ?? 0) / 2,
  };
}

/**
 * True when meta.frameId is empty or does not resolve to any page-level frame.
 * Used so organize pins that never got membership (or keep a stale id) still
 * travel with the frame they visually sit in.
 */
function isOrphanedPinFrameMembership(editor, frameId) {
  if (frameId == null || frameId === '') return true;
  const key = String(frameId);
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape.type !== 'frame') continue;
    if (String(membershipIdFromFrameShape(shape)) === key) return false;
  }
  return true;
}

/**
 * Pins that should travel with a frame move:
 * - already children of the frame (tldraw moves them; we only refresh meta/placements)
 * - page-level pins with matching meta.frameId (set when cursor is released inside the frame)
 * - page-level pins with no/orphaned membership whose center lies inside the frame
 *   (covers AI organize when meta.frameId was not written)
 *
 * When prevFrameShape differs (frame drag), spatial checks use that record's bounds.
 * Callers that drag a frame must capture this set once: later steps of the same
 * drag would otherwise adopt every pin the rectangle passes over.
 */
export function findPinsAssociatedWithFrame(editor, frameShape, prevFrameShape = frameShape) {
  const pageId = editor.getCurrentPageId();
  const membershipId = membershipIdFromFrameShape(frameShape);
  const spatialBounds = framePageBounds(editor, prevFrameShape || frameShape);
  const associated = [];

  for (const shape of editor.getCurrentPageShapes()) {
    if (!isBoardPinShape(shape)) continue;

    if (shape.parentId === frameShape.id) {
      associated.push({ shape, alreadyChild: true });
      continue;
    }

    if (shape.parentId !== pageId) continue;

    const byMeta =
      Boolean(membershipId) &&
      Boolean(shape.meta?.frameId) &&
      String(shape.meta.frameId) === String(membershipId);

    if (byMeta) {
      associated.push({ shape, alreadyChild: false });
      continue;
    }

    if (!isOrphanedPinFrameMembership(editor, shape.meta?.frameId)) continue;
    const center = pinPageCenter(editor, shape);
    if (!pointInBounds(center.x, center.y, spatialBounds)) continue;
    associated.push({ shape, alreadyChild: false });
  }

  return associated;
}

/**
 * Smallest page-level frame whose bounds contain the page point.
 */
export function findContainingFrameShape(editor, pageX, pageY) {
  const pageId = editor.getCurrentPageId();
  let best = null;
  let bestArea = Infinity;

  for (const shape of editor.getCurrentPageShapes()) {
    if (shape.type !== 'frame' || shape.parentId !== pageId) continue;
    const bounds = framePageBounds(editor, shape);
    if (!pointInBounds(pageX, pageY, bounds)) continue;
    const area = (bounds.w ?? 0) * (bounds.h ?? 0);
    if (area < bestArea) {
      best = shape;
      bestArea = area;
    }
  }

  return best;
}

/**
 * Update meta.frameId from spatial membership without reparenting.
 * Pins stay page-level so frames do not clip them.
 * Prefer dropPoint (cursor page coords on pointer-up); fall back to pin center.
 */
export function syncPinFrameMembership(editor, shapeId, dropPoint = null) {
  if (!editor || !shapeId) return null;

  const shape = editor.getShape(shapeId);
  if (!shape || !isBoardPinShape(shape)) return null;

  // If already a true frame child (legacy / native drop), keep that relationship.
  const parent = shape.parentId ? editor.getShape(shape.parentId) : null;
  if (parent?.type === 'frame') {
    const nextFrameId = membershipIdFromFrameShape(parent);
    if ((shape.meta?.frameId || null) !== (nextFrameId || null)) {
      editor.updateShape({
        id: shapeId,
        type: shape.type,
        meta: { ...shape.meta, frameId: nextFrameId },
      });
    }
    return nextFrameId;
  }

  const point =
    dropPoint && Number.isFinite(dropPoint.x) && Number.isFinite(dropPoint.y)
      ? dropPoint
      : pinPageCenter(editor, shape);
  const containing = findContainingFrameShape(editor, point.x, point.y);
  const nextFrameId = containing ? membershipIdFromFrameShape(containing) : null;
  if ((shape.meta?.frameId || null) === (nextFrameId || null)) return nextFrameId;

  editor.updateShape({
    id: shapeId,
    type: shape.type,
    meta: {
      ...shape.meta,
      frameId: nextFrameId,
    },
  });
  return nextFrameId;
}

/**
 * Sync frame membership for one or more pin shapes after a drag.
 * @param {{ x: number, y: number } | null} dropPoint cursor page point on pointer-up
 */
export function syncPinFrameMembershipForShapes(editor, shapeIds = [], dropPoint = null) {
  if (!editor || !shapeIds?.length) return;
  const unique = [...new Set(shapeIds.filter(Boolean))];
  for (const shapeId of unique) {
    syncPinFrameMembership(editor, shapeId, dropPoint);
  }
}

/**
 * Which page-level pins translate with a frame move.
 * A drag locks the set on the first step (existing members, plus pins already
 * inside where the drag started) and reuses it until pointer-up. Moving the
 * frame does not adopt pins the rectangle merely touches.
 */
export function createFrameDragFollowSession() {
  const byFrameId = new Map();

  return {
    pinIdsForFrameStep(editor, prevFrame, nextFrame, { isDragging = false, originKey = '' } = {}) {
      const frameId = nextFrame?.id;
      if (!frameId) return [];

      const existing = byFrameId.get(frameId);
      const continuingDrag = Boolean(
        existing && originKey && existing.originKey === originKey
      );

      let ids;
      if (continuingDrag) {
        ids = existing.ids;
      } else {
        ids = findPinsAssociatedWithFrame(editor, nextFrame, prevFrame)
          .filter(entry => !entry.alreadyChild)
          .map(entry => entry.shape.id);
        if (isDragging && originKey) {
          byFrameId.set(frameId, { originKey, ids });
        } else {
          byFrameId.delete(frameId);
        }
      }

      if (!isDragging) byFrameId.delete(frameId);
      return ids;
    },
  };
}
